import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { visuallyHidden } from "@mui/utils";
import ChevronRight from "@mui/icons-material/ChevronRight";
import LibraryAddCheckOutlined from "@mui/icons-material/LibraryAddCheckOutlined";
import MailOutline from "@mui/icons-material/MailOutline";
import Search from "@mui/icons-material/Search";

import agent from "../../utils/axios";
import { formatDate, formatMoney } from "../../utils/format";
import { monoFontFamily } from "../../theme/tokens";
import { lastPageIndex } from "../../utils/paging";
import {
  billChipMeta,
  Callout,
  ConfirmDialog,
  PayeeCell,
  StatusChip,
  useToast,
} from "../../components/ui";
import CompanySelectDialog, { QuickBooksCompany } from "./CompanySelectDialog";
import { useConvertToCheck } from "./useConvertToCheck";
import {
  attentionTitle,
  billProblem,
  BillSelection,
  convertedMessage,
  deselectRows,
  errorMessageOf,
  isSelectable,
  pageSelectionState,
  selectedRefCodes,
  selectionTotal,
  selectRows,
  toggleRow,
} from "./billHelpers";

type RowType = {
  payeeId: string;
  payeeName: string;
  invoiceDate: string | null;
  invoiceId: string | null;
  billRefCode: string;
  paymentRefCode: string;
  billStatus: string;
  billAmount: number;
  paymentStatus: string;
  paymentMethod: string | null;
  errorMessage?: string | null;
};

/** 'attention' = Failed or New (default); 'unpaid' = every bill not yet Paid (the old list). */
type Scope = "attention" | "unpaid";

type Order = "asc" | "desc";

// The list's payee id is projected as `_id`, so that is the field the ID sort must name.
const PAYEE_ID_SORT = "_id";

type SortableColumn = {
  id: string;
  label: string;
  align?: "left" | "right";
};

// Every header stays sortable (Payee covers name and ID; Problem sorts by the stored error).
const columns: SortableColumn[] = [
  { id: "invoiceId", label: "Invoice" },
  { id: "invoiceDate", label: "Invoice date" },
  { id: "billRefCode", label: "Bill ref" },
  { id: "billStatus", label: "Status" },
  { id: "billAmount", label: "Amount", align: "right" },
  { id: "paymentMethod", label: "Method" },
  { id: "errorMessage", label: "Problem" },
];

const COLUMN_COUNT = columns.length + 3; // checkbox, payee, view

const SEARCH_DEBOUNCE_MS = 300;

interface PendingBillsProps {
  /** Total bills needing attention (the tab badge); null while unknown. */
  attentionCount: number | null;
  /** Called with the total whenever a Needs attention ('attention' scope) page loads. */
  onAttentionCount?: (count: number) => void;
  /** Called after Convert to check so the tab badge can be refreshed. */
  onConverted?: () => void;
}

const PendingBills = ({
  attentionCount,
  onAttentionCount,
  onConverted,
}: PendingBillsProps) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState("billStatus");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState<RowType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [scope, setScope] = useState<Scope>("attention");
  const [reloadKey, setReloadKey] = useState(0);
  // Selected ROWS keyed by billRefCode, so names and amounts survive paging (AB#5377:
  // the key also de-duplicates a refcode shared by several rows).
  const [selection, setSelection] = useState<BillSelection<RowType>>(
    () => new Map()
  );
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);
  const { convert, busy: converting } = useConvertToCheck();

  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const onAttentionCountRef = useRef(onAttentionCount);
  onAttentionCountRef.current = onAttentionCount;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    if (searchInput === searchTerm) return;
    const handle = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput, searchTerm]);

  const loadBills = useCallback(async () => {
    const requestId = ++requestRef.current;
    let movedPage = false;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await agent.Bills.getAllFailedBills(
        page,
        rowsPerPage,
        orderBy,
        order,
        searchTerm,
        scope
      );
      // Ignore a response that a newer request (or leaving the tab) has replaced.
      if (!mountedRef.current || requestId !== requestRef.current) return;
      const rows: RowType[] = Array.isArray(response?.data)
        ? response.data
        : [];
      const count = Number(response?.count);
      if (scope === "attention" && Number.isFinite(count)) {
        onAttentionCountRef.current?.(count);
      }
      // The page emptied since it was chosen (bills converted, paid or resent, by this
      // screen or anyone else): go to the last page that still has bills. Changing the page
      // loads it; keep the bar up until then.
      const lastPage = lastPageIndex(count, rowsPerPage);
      if (rows.length === 0 && Number.isFinite(count) && page > lastPage) {
        movedPage = true;
        setPage(lastPage);
        return;
      }
      setData(rows);
      setTotalCount(Number.isFinite(count) ? count : rows.length);
    } catch (error) {
      if (!mountedRef.current || requestId !== requestRef.current) return;
      setData([]);
      setTotalCount(0);
      setLoadError(errorMessageOf(error, "The bills could not be loaded."));
    } finally {
      if (
        !movedPage &&
        mountedRef.current &&
        requestId === requestRef.current
      ) {
        setLoading(false);
      }
    }
  }, [page, rowsPerPage, orderBy, order, searchTerm, scope]);

  useEffect(() => {
    loadBills();
  }, [loadBills, reloadKey]);

  const reload = () => setReloadKey((key) => key + 1);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0); // Reset to the first page on sort change
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleScopeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setScope(event.target.checked ? "unpaid" : "attention");
    setPage(0);
  };

  // Selection
  const pageState = pageSelectionState(selection, data);
  const selectedCount = selection.size;
  const selectedTotal = selectionTotal(selection);

  const handleSelectAllClick = () => {
    setSelection((current) =>
      pageSelectionState(current, data).checked
        ? deselectRows(current, data)
        : selectRows(current, data)
    );
  };

  const handleRowToggle = (row: RowType) => {
    setSelection((current) => toggleRow(current, row));
  };

  const clearSelection = () => setSelection(new Map());

  const dialogBills = useMemo(
    () =>
      Array.from(selection.values()).map((row) => ({
        name: (row.payeeName ?? "").trim(),
        amount: row.billAmount,
      })),
    [selection]
  );

  // Convert to check: pick the QuickBooks company, then convert with CSI Pay's stored token
  // (or the sign-in popup when the company needs one).
  const handleCompanyConfirm = async (company: QuickBooksCompany) => {
    const refCodes = selectedRefCodes(selection);
    if (refCodes.length === 0) return;
    try {
      const outcome = await convert(refCodes, company.realmId);
      if (!mountedRef.current) return;
      setCompanyDialogOpen(false);
      if (outcome === "signInIncomplete") {
        toast.error(
          "QuickBooks sign-in didn't finish, so no bills were converted."
        );
        return;
      }
      toast.success(convertedMessage(refCodes.length));
      clearSelection();
      reload();
      onConverted?.();
    } catch (error) {
      if (!mountedRef.current) return;
      setCompanyDialogOpen(false);
      setConvertError(
        errorMessageOf(error, "QuickBooks didn't accept the conversion.")
      );
      clearSelection();
      reload();
      onConverted?.();
    }
  };

  const emptyMessage = searchTerm
    ? `No bills match "${searchTerm}".`
    : scope === "attention"
    ? "No bills need attention."
    : "Every bill has been paid.";

  return (
    <Box sx={{ display: "grid", gap: 4 }}>
      {attentionCount !== null && (
        <Callout
          tone={attentionCount > 0 ? "warn" : "ok"}
          title={attentionTitle(attentionCount)}
        >
          {attentionCount > 0
            ? "Fix the payee in Tipalti and resend, or convert the bill to a check."
            : "No bills have failed or are waiting to be sent."}
        </Callout>
      )}

      <Card>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 3,
            p: 4,
          }}
        >
          <TextField
            size="small"
            label="Search bills"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by Payee ID, Name, Bill Ref Code..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: "1 1 320px", maxWidth: 550 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={scope === "unpaid"}
                onChange={handleScopeChange}
              />
            }
            label="Include bills still with Tipalti"
          />
          {selectedCount === 0 && (
            <Button
              variant="contained"
              startIcon={<MailOutline />}
              disabled
              sx={{ ml: "auto" }}
            >
              Convert to check…
            </Button>
          )}
        </Box>

        {selectedCount > 0 && (
          <Box
            role="region"
            aria-label="Selected bills"
            sx={(theme) => ({
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 3,
              px: 4,
              py: 2,
              backgroundColor: theme.palette.brandTint,
              borderTop: 1,
              borderColor: "divider",
            })}
          >
            <LibraryAddCheckOutlined
              fontSize="small"
              sx={{ color: "primary.main" }}
            />
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              <Box component="strong" sx={{ fontWeight: 500 }}>
                {selectedCount.toLocaleString("en-US")} selected
              </Box>{" "}
              · {formatMoney(selectedTotal)}
            </Typography>
            <Button size="small" onClick={clearSelection}>
              Clear selection
            </Button>
            <Button
              variant="contained"
              startIcon={<MailOutline />}
              onClick={() => setCompanyDialogOpen(true)}
              disabled={converting}
              sx={{ ml: "auto" }}
            >
              Convert to check…
            </Button>
          </Box>
        )}

        {loadError && (
          <Box sx={{ px: 4, pb: 3 }}>
            <Callout
              tone="err"
              role="alert"
              title="The bills could not be loaded"
              action={
                <Button color="inherit" size="small" onClick={reload}>
                  Retry
                </Button>
              }
            >
              {loadError}
            </Callout>
          </Box>
        )}

        {/* Reserve the bar's height so the table doesn't jump while loading. */}
        <Box sx={{ height: 4 }}>
          {loading && <LinearProgress aria-label="Loading bills" />}
        </Box>

        <TableContainer>
          <Table aria-label="Bills that need attention">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={pageState.indeterminate}
                    checked={pageState.checked}
                    disabled={pageState.selectable === 0}
                    onChange={handleSelectAllClick}
                    inputProps={{
                      "aria-label": "select all bills on this page",
                    }}
                  />
                </TableCell>
                <TableCell
                  sortDirection={
                    orderBy === "payeeName" || orderBy === PAYEE_ID_SORT
                      ? order
                      : false
                  }
                >
                  <TableSortLabel
                    active={orderBy === "payeeName"}
                    direction={orderBy === "payeeName" ? order : "asc"}
                    onClick={() => handleRequestSort("payeeName")}
                  >
                    Payee
                  </TableSortLabel>
                  <TableSortLabel
                    active={orderBy === PAYEE_ID_SORT}
                    direction={orderBy === PAYEE_ID_SORT ? order : "asc"}
                    onClick={() => handleRequestSort(PAYEE_ID_SORT)}
                    aria-label="Sort by payee ID"
                    sx={{ ml: 2 }}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align ?? "left"}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right">
                  <span style={visuallyHidden}>View</span>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((row, index) => {
                const selectable = isSelectable(row);
                const isItemSelected =
                  selectable && selection.has(row.billRefCode);
                const problem = billProblem(row);
                const payeeName = (row.payeeName ?? "").trim();
                return (
                  <TableRow
                    key={`${row.billRefCode || "no-ref"}-${index}`}
                    hover
                    aria-selected={isItemSelected}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        disabled={!selectable}
                        onChange={() => handleRowToggle(row)}
                        inputProps={{
                          "aria-label": selectable
                            ? `Select bill ${row.billRefCode}`
                            : "This bill has no ref code and can't be converted",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <PayeeCell
                        name={payeeName || "Unnamed payee"}
                        id={row.payeeId}
                        to={
                          row.payeeId
                            ? `/payees/detail/${row.payeeId}`
                            : undefined
                        }
                      />
                    </TableCell>
                    <TableCell>{row.invoiceId || "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(row.invoiceDate)}
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: monoFontFamily,
                          fontSize: "0.75rem",
                          color: "text.secondary",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.billRefCode || "—"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        size="small"
                        meta={billChipMeta(row.billStatus, row.paymentStatus)}
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatMoney(row.billAmount)}
                    </TableCell>
                    <TableCell>
                      {row.paymentMethod ? (
                        <StatusChip
                          size="small"
                          tone="neutral"
                          label={row.paymentMethod}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      {problem ? (
                        <Tooltip title={problem.text}>
                          <Box
                            sx={(theme) => ({
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color:
                                problem.kind === "failed"
                                  ? theme.palette.status.err.fg
                                  : theme.palette.text.secondary,
                            })}
                          >
                            {problem.text}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Box component="span" sx={{ color: "text.secondary" }}>
                          —
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        component={RouterLink}
                        to={`/bills/detail/${row.billRefCode}`}
                        disabled={!row?.billRefCode}
                        endIcon={<ChevronRight />}
                        aria-label={
                          row.billRefCode
                            ? `View bill ${row.billRefCode}`
                            : "No bill ref code to view"
                        }
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {data.length === 0 && !loadError && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    align="center"
                    sx={{ py: 10, color: "text.secondary" }}
                  >
                    {loading ? "Loading bills…" : emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 250]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <CompanySelectDialog
        open={companyDialogOpen}
        bills={dialogBills}
        busy={converting}
        onClose={() => setCompanyDialogOpen(false)}
        onConfirm={handleCompanyConfirm}
      />

      <ConfirmDialog
        open={convertError !== null}
        variant="notice"
        tone="err"
        title="Convert to check didn't finish"
        body={
          <>
            {convertError}
            <br />
            Some bills may already have been converted, so the list has been
            refreshed. Check the bills before trying again.
          </>
        }
        confirmLabel="Close"
        onConfirm={() => setConvertError(null)}
        onClose={() => setConvertError(null)}
      />
    </Box>
  );
};

export default PendingBills;
